import {Visibility} from "@mui/icons-material";
import {
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	CardHeader,
	Chip,
	Grid,
	IconButton,
	List,
	ListItem,
	ListItemIcon,
	ListItemSecondaryAction,
	ListItemText,
	Stack,
	styled,
	Typography,
} from "@mui/material";
import {useLocalization, useWordingFlavor} from "@smalox/shared";
import {useQuery} from "@tanstack/react-query";
import {
	BreadcrumbItem,
	BreadcrumbLink,
	Breadcrumbs,
	ConfirmButton,
	Now,
	Tabs,
	TemporalFormat,
	TemporalRangeFormat,
	tryParseInstant,
} from "@variocube/app-ui";
import React, {useRef} from "react";
import {useAsync} from "react-async-hook";
import {Link} from "react-router-dom";
import {Booking, BookingUser, TenantUserSummary, useBookingApi, UserRef} from "../../api";
import {useBookingShareApi} from "../../api/bookingShare";
import {UserIcon} from "../../icons";
import {useCanManageBookingAccess, useTenantFeature, useTenantId} from "../../tenant";
import {queryKeys} from "../../utils/queryKeys";
import {useSearchParam} from "../../utils/search";
import {useNumericParam} from "../../utils/useParam";
import {AuditTimeline} from "../audit/AuditTimeline";
import {CompartmentLinkIconButton} from "../compartment/CompartmentLinkIconButton";
import {LocationLinkChip} from "../location/LocationChip";
import {View, ViewHeader} from "../View";
import {BookingStatusChip} from "./BookingStatusChip";
import {BookingUserAddButton} from "./BookingUserAddButton";
import {BookingUserRemoveButton} from "./BookingUserRemoveButton";
import {BookingShareList} from "./share/BookingShareList";

const FullHeightCard = styled(Card)`
  height: 100%;
`;

export function BookingView() {
	const {t} = useLocalization();
	const wordingFlavor = useWordingFlavor();
	const tenantId = useTenantId();
	const bookingId = useNumericParam("bookingId");
	const {getBooking, addBookingUser, removeBookingUser, updateBookingUntil, getBookingAuditLog} = useBookingApi();
	const {countBookingShares} = useBookingShareApi();
	const {loading, error, result: booking, execute} = useAsync(getBooking, [bookingId]);
	const reloadAudit = useRef(() => Promise.resolve());

	const canShowBookingShares = useTenantFeature("SHOW_BOOKING_SHARES");
	const canManageBookingAccess = useCanManageBookingAccess(booking);

	const [tab, setTab] = useSearchParam("tab", "details");

	const from = tryParseInstant(booking?.from);
	const until = tryParseInstant(booking?.until);

	async function reload() {
		await Promise.all([execute(bookingId), reloadAudit.current()]);
	}

	async function handleAddUsers(users: TenantUserSummary[]) {
		for (const user of users) {
			await addBookingUser(bookingId, user.user.username);
		}
		await reload();
	}

	async function handleRemoveUser(user: BookingUser) {
		await removeBookingUser(bookingId, user.user!.username);
		await reload();
	}

	async function handleEndBooking() {
		if (booking) {
			await updateBookingUntil(bookingId, Now.instant().toString());
			await reload();
		}
	}

	const {data: bookingShareCount} = useQuery(
		queryKeys.bookingShares.count(
			tenantId,
			bookingId,
		),
		() => countBookingShares(bookingId),
		{
			staleTime: 60_000,
			enabled: canShowBookingShares,
		},
	);

	const areTabsEnabled = canShowBookingShares;

	return (
		<View loading={loading} error={error}>
			<Breadcrumbs>
				<BreadcrumbLink component={Link} to={".."}>{t(`bookings.title.${wordingFlavor}`)}</BreadcrumbLink>
				<BreadcrumbItem>{booking?.id}</BreadcrumbItem>
			</Breadcrumbs>
			<ViewHeader
				title={t(`bookings.view.title.${wordingFlavor}`, booking)}
				subTitle={
					<Stack spacing={1} direction="row" alignItems="center">
						<Box>
							{from && until && <TemporalRangeFormat from={from} until={until} />}
							{from && !until && <TemporalFormat value={from} />}
						</Box>
						{booking?.selfManaged && <Chip label={t("bookings.selfManaged.title")} />}
						{booking?.temporary && <Chip label={t("bookings.temporary.title")} />}
					</Stack>
				}
				titleAdornment={booking && <BookingStatusChip {...booking} />}
				actions={booking?.status == "ACTIVE" && (
					<Stack direction="row" spacing={2}>
						{canManageBookingAccess && (
							<>
								{canShowBookingShares && (
									<Button variant="outlined" color="secondary" component={Link} to="newShare">
										{t(`bookings.view.newShare.${wordingFlavor}`)}
									</Button>
								)}
								<Button variant="outlined" color="primary" component={Link} to="edit">
									{t("edit")}
								</Button>
							</>
						)}
						<ConfirmButton
							variant="outlined"
							cancel={t("cancel")}
							title={t(`bookings.end.title.${wordingFlavor}`)}
							onConfirm={handleEndBooking}
						>
							{t(`bookings.end.message.${wordingFlavor}`)}
						</ConfirmButton>
					</Stack>
				)}
			/>

			{areTabsEnabled && (
				<Tabs
					value={tab}
					onChange={(_, tab) => setTab(tab)}
					items={[
						{label: t("bookings.view.details"), value: "details"},
						{
							label: (
								<Box sx={{display: "flex", alignItems: "center", gap: 1}}>
									{t(`bookings.shares.title.${wordingFlavor}`)}{" "}
									<Chip label={bookingShareCount?.count ?? 0} />
								</Box>
							),
							value: "bookingShares",
						},
					]}
				/>
			)}

			{(!areTabsEnabled || tab == "details")
				&& (
					<>
						<Box>
							<Grid container spacing={2}>
								{booking
									&& (
										<Grid item xs={12} md={6} xl={4}>
											<BookingDetailsCard booking={booking} />
										</Grid>
									)}
								<Grid item xs={12} md={6} xl={4}>
									<FullHeightCard>
										<CardHeader title={t(`bookings.compartment.${wordingFlavor}`)} />
										<CardActions sx={{flexWrap: "wrap"}}>
											{booking && <CompartmentLinkIconButton compartment={booking.compartment} />}
											{booking && <LocationLinkChip location={booking.location} />}
										</CardActions>
									</FullHeightCard>
								</Grid>
								<Grid item xs={12} md={6} xl={4}>
									<FullHeightCard>
										<CardHeader title={t("bookings.users.title")} />
										<List>
											{booking?.users.map(bu => (
												<ListItem
													key={bu.user.username}
												>
													<ListItemIcon>
														<UserIcon />
													</ListItemIcon>
													<ListItemText
														primary={bu.user.displayName}
														secondary={bu.user.email}
													/>
													<ListItemSecondaryAction>
														{bu.tenantMember !== false && (
															<IconButton
																component={Link}
																to={`/${tenantId}/users/${bu.user.username}`}
															>
																<Visibility />
															</IconButton>
														)}
														<BookingUserRemoveButton
															user={bu}
															onRemove={handleRemoveUser}
														/>
													</ListItemSecondaryAction>
												</ListItem>
											))}
										</List>
										<CardActions>
											<BookingUserAddButton onAddUsers={handleAddUsers} />
										</CardActions>
									</FullHeightCard>
								</Grid>
							</Grid>
						</Box>
						<Box pt={4}>
							<Typography variant="h2" textAlign="center">{t("audit.title")}</Typography>
							<AuditTimeline
								load={getBookingAuditLog}
								param={bookingId}
								reloadRef={reload => {
									reloadAudit.current = reload;
								}}
							/>
						</Box>
					</>
				)}

			{canShowBookingShares && tab == "bookingShares"
				&& booking && <BookingShareList booking={booking} />}
		</View>
	);
}

function BookingDetailsCard({booking}: Readonly<{ booking: Booking }>) {
	const {t} = useLocalization();
	const wordingFlavor = useWordingFlavor();
	return (
		<FullHeightCard>
			<CardHeader title={t(`bookings.view.name.${wordingFlavor}`)} />
			<CardContent>
				{booking.name
					? <Typography>{booking.name}</Typography>
					: (
						<Typography variant={"body2"} fontStyle={"italic"}>
							{t("bookings.view.noNameProvided")}
						</Typography>
					)}
			</CardContent>
		</FullHeightCard>
	);
}
